/**
 * LegalDocument — renders the Terms / Privacy markdown files.
 *
 * Public route (no auth required). The markdown source lives at
 * `docs/legal/*.md` and is bundled at build-time via Vite `?raw` imports.
 *
 * Sprint 0 Tier 1 Sec2 — LGPD compliance.
 */
import { useEffect, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "@phosphor-icons/react";
import { LegalLinks } from "@/components/shared/LegalLinks";
// Vite-specific `?raw` imports — typed via `vite/client` reference in src/vite-env.d.ts.
import termsMd from "../../docs/legal/termos-de-uso.md?raw";
import privacyMd from "../../docs/legal/politica-de-privacidade.md?raw";

const DOCS: Record<string, { title: string; markdown: string }> = {
  termos: { title: "Termos de Uso", markdown: termsMd },
  privacidade: { title: "Política de Privacidade", markdown: privacyMd },
};

type LegalMarkdownNode = {
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: LegalMarkdownNode[];
};

// Match the Unicode-preserving fragments used by the documents' table of contents.
function addLegalHeadingIds() {
  return (tree: LegalMarkdownNode) => {
    const usedIds = new Set<string>();
    const textOf = (node: LegalMarkdownNode): string =>
      node.value ?? node.children?.map(textOf).join("") ?? "";

    const visit = (node: LegalMarkdownNode) => {
      if (node.tagName && /^h[1-6]$/.test(node.tagName)) {
        const base = textOf(node)
          .toLowerCase()
          .replace(/[^\p{L}\p{N}_\-\s]/gu, "")
          .replace(/\s/g, "-");
        let id = base;
        let suffix = 1;
        while (usedIds.has(id)) id = `${base}-${suffix++}`;
        usedIds.add(id);
        node.properties = { ...node.properties, id, tabIndex: -1 };
      }
      node.children?.forEach(visit);
    };
    visit(tree);
  };
}

export default function LegalDocument() {
  const { slug } = useParams<{ slug: string }>();
  const { hash } = useLocation();
  const doc = slug ? DOCS[slug] : undefined;
  const previousDocument = useRef<typeof doc>();

  useEffect(() => {
    if (doc) document.title = `${doc.title} — Código do Poder`;
  }, [doc]);

  useEffect(() => {
    const hadPreviousDocument = Boolean(previousDocument.current);
    const documentChanged = previousDocument.current !== doc;
    previousDocument.current = doc;
    if (!doc) return;
    if (!hash) {
      if (!documentChanged) return;
      const title = document.querySelector<HTMLElement>("#documento-legal article h1");
      document.getElementById("documento-legal")?.scrollIntoView?.({ block: "start" });
      if (hadPreviousDocument) title?.focus({ preventScroll: true });
      return;
    }
    let id: string;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return;
    }
    const heading = document.getElementById(id);
    heading?.scrollIntoView?.({ block: "start" });
    heading?.focus({ preventScroll: true });
  }, [doc, hash]);

  if (!doc) {
    return (
      <main className="min-h-screen bg-background p-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl font-semibold text-foreground">Documento não encontrado.</h1>
          <Link to="/" className="text-primary hover:underline mt-4 inline-block">
            Voltar
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background p-4 sm:p-6" id="documento-legal">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex min-h-11 items-center gap-2 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft weight="bold" className="w-4 h-4" aria-hidden="true" />
          <span>Voltar</span>
        </Link>
        <LegalLinks className="mb-8 justify-start border-b border-border pb-4" />
        <article className={[
          "break-words text-[1rem] leading-[1.7] text-foreground",
          "[&_h1]:mb-6 [&_h1]:scroll-mt-6 [&_h1]:text-[1.75rem] [&_h1]:font-bold [&_h1]:leading-[1.2] sm:[&_h1]:text-[2rem]",
          "[&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:scroll-mt-6 [&_h2]:text-[1.25rem] [&_h2]:font-semibold [&_h2]:leading-[1.4]",
          "[&_h3]:mb-3 [&_h3]:mt-6 [&_h3]:scroll-mt-6 [&_h3]:text-[1.125rem] [&_h3]:font-semibold [&_h3]:leading-[1.4]",
          "[&_p]:my-4 [&_strong]:font-semibold [&_em]:italic",
          "[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-2 [&_li>p]:my-2",
          "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-foreground [&_a]:focus-visible:outline [&_a]:focus-visible:outline-2 [&_a]:focus-visible:outline-ring",
          "[&_hr]:my-8 [&_hr]:border-border [&_blockquote]:my-6 [&_blockquote]:border-l [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground",
          "[&_table]:w-full [&_table]:border-collapse [&_table]:text-[0.875rem] [&_table]:leading-relaxed [&_th]:text-left [&_tr]:border-b [&_tr]:border-border [&_tbody_tr:last-child]:border-b-0",
        ].join(" ")}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[addLegalHeadingIds]}
            components={{
              table: ({ children }) => (
                <div
                  role="region"
                  aria-label="Tabela do documento — role horizontalmente para ver todas as colunas"
                  tabIndex={0}
                  className="my-6 overflow-x-auto rounded-lg border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <table className="!m-0 min-w-[36rem] text-sm text-foreground">{children}</table>
                </div>
              ),
              th: ({ children }) => (
                <th scope="col" className="!border-border bg-muted !px-4 !py-3 !text-foreground">{children}</th>
              ),
              td: ({ children }) => (
                <td className="!border-border !px-4 !py-3 align-top">{children}</td>
              ),
            }}
          >
            {doc.markdown}
          </ReactMarkdown>
        </article>
        <footer className="mt-10 border-t border-border py-6">
          <LegalLinks className="justify-start" />
        </footer>
      </div>
    </main>
  );
}
